import React, { memo } from 'react';
import { FunctionField, ReferenceField, TextField, useTranslate } from 'react-admin';
import { Box, IconButton, Typography, useTheme } from '@material-ui/core';
import moment from 'moment';
import { OpenInNew } from '@material-ui/icons';
import resourceSlug from '../../../../constant/resource-slug';
import DrawerSimpleShowLayout from '../../../../base/components/ra/drawers/DrawerSimpleShowLayout';
import { AmountField, DateField } from '../../../../base/components/ra/fields';
import Labeled from '../../../../base/components/ra/labeled';
import { DATE_DISPLAY_FORMAT } from '../../../../constant';
import { INVOICE_SERVICE, PAYMENT_STATUS } from '../../../../constant/invoice';
import { getAddressLines } from '../../../group/group.show';
import { formatMoney } from '../../../../services/util/formatNumber';
import { formatDate } from '../../../../services/util/date';

const GeneralInfo = memo(({ record }) => {
  const theme = useTheme();
  const t = useTranslate();

  return (
    <Box
      display="flex"
      sx={{
        gap: 12,
      }}
      flexDirection={{
        xs: 'column',
        lg: 'row',
      }}
    >
      <Box
        component={DrawerSimpleShowLayout}
        width={{
          xs: '100%',
          lg: '40%',
        }}
        sx={{
          bgcolor: 'white',
          borderRadius: 12,
          padding: '24px !important',
        }}
        record={record}
      >
        <FunctionField
          label={<Typography variant="h4">{t('resources.invoice.details')}</Typography>}
          render={(record) => <Typography variant="h4">{record?.invoice?.currencyCode}</Typography>}
        />
        <Labeled
          label={
            <Box
              display="flex"
              alignItems="center"
              sx={{
                gap: 4,
              }}
            >
              {t(`resources.invoice.fields.charge.${INVOICE_SERVICE.MONTHLY_GGR}`)}
              {record?.invoice?.ggrAmount !== 0 && <ScrollButton targetId="ggrCharge" />}
            </Box>
          }
        >
          <AmountField source="invoice.ggrAmount" />
        </Labeled>
        {!!record?.invoice?.mktAmount && (
          <Labeled
            label={
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  gap: 4,
                }}
              >
                {t(`resources.invoice.fields.charge.${INVOICE_SERVICE.MKT_FREE_SPIN}`)}
                <ScrollButton targetId="mktFreeSpin" />
              </Box>
            }
          >
            <AmountField source="invoice.mktAmount" />
          </Labeled>
        )}
        {!!record?.invoice?.dailyProgramAmount && (
          <Labeled
            label={
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  gap: 4,
                }}
              >
                {t(`resources.invoice.fields.charge.${INVOICE_SERVICE.DAILY_PROGRAM}`)}
                <ScrollButton targetId="dailyProgram" />
              </Box>
            }
          >
            <AmountField source="invoice.dailyProgramAmount" />
          </Labeled>
        )}

        <Labeled label={t('resources.invoice.fields.carryForward')}>
          <AmountField source="invoice.carryForward" />
        </Labeled>
        {!!record?.invoice?.adjustment?.id && (
          <Labeled
            label={
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  gap: 4,
                }}
              >
                {record?.invoice?.adjustment?.description}
              </Box>
            }
          >
            <AmountField source="invoice.adjustment.amount" />
          </Labeled>
        )}
        <FunctionField
          label={<Typography variant="h4">{t('resources.invoice.total')}</Typography>}
          render={(record) => <Typography variant="h4">{formatMoney(record?.invoice?.invoiceAmount)}</Typography>}
        />
      </Box>
      <Box
        component={DrawerSimpleShowLayout}
        sx={{
          flexGrow: 1,
          bgcolor: 'white',
          borderRadius: 12,
          padding: '24px !important',
        }}
        record={record}
      >
        <FunctionField
          label="resources.invoice.fields.invoicePeriod"
          render={(record) =>
            `${formatDate(record.invoice?.invoiceFrom, DATE_DISPLAY_FORMAT)} - ${formatDate(record.invoice?.invoiceTo, DATE_DISPLAY_FORMAT)}`
          }
        />
        <ReferenceField
          label="resources.group.name"
          sortable={false}
          reference={resourceSlug.GROUP}
          source="invoice.groupId"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField label="resources.group.fields.companyName" source="companyProfile.companyName" emptyText="-" />
        <FunctionField
          label="resources.group.fields.companyAddress"
          render={(record) =>
            [
              getAddressLines(record?.companyProfile || {}),
              record?.companyProfile?.city,
              record?.companyProfile?.country,
            ]
              .filter((line) => !!line?.trim?.())
              .join(', ') || '-'
          }
        />
        <TextField label="resources.group.fields.regNo" source="companyProfile.regNo" emptyText="-" />
        <Labeled label={t('resources.invoice.fields.invoiceDate')}>
          <DateField source="invoice.invoiceDate" dateFormat={DATE_DISPLAY_FORMAT} record={record} />
        </Labeled>

        <FunctionField
          label="resources.invoice.fields.dueDate"
          render={(record) =>
            record?.invoice?.dueDate ? (
              <Box
                color={
                  moment.unix(record?.invoice?.dueDate).isSameOrBefore(moment()) &&
                  record.paymentStatus !== PAYMENT_STATUS.PAID
                    ? theme.palette.error.main
                    : undefined
                }
              >
                {formatDate(record?.invoice?.dueDate, DATE_DISPLAY_FORMAT)}
              </Box>
            ) : (
              '-'
            )
          }
        />
      </Box>
    </Box>
  );
});

export default GeneralInfo;

const ScrollButton = ({ targetId }) => {
  const handleClick = (event) => {
    event.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      const header = document.getElementsByTagName('header')[0];
      const top =
        element.getBoundingClientRect().top + window.scrollY - (header?.getBoundingClientRect?.()?.bottom || 0);

      window.scrollTo({
        behavior: 'smooth',
        top,
      });
    }
  };

  return (
    <Box
      component={IconButton}
      variant="text"
      size="small"
      onClick={handleClick}
      width={24}
      height={24}
      sx={{
        '& svg': {
          width: 16,
        },
      }}
    >
      <OpenInNew />
    </Box>
  );
};
